/**
 * System of a Down — Toxicity · songs.
 *
 * Source of truth: `/Users/ivanmunguia/projects/nyx/tmp/nyx_landing_soad_songs.json`.
 * Analytical text is forwarded verbatim except for schema-fit flattening:
 *
 *   - `n`           : track position from the album drop, not the song-index
 *                     from the generated song JSON.
 *   - `duration`    : album-track duration formatted as M:SS.
 *   - `perspective` : flattened from `perspective.description`.
 *   - `voice`       : flattened from `perspective.voice`.
 *   - `alternative` : omitted when `alternative_readings` is empty.
 *   - `live`        : `live_tips` renamed.
 *
 * The JSON also ships `perspective.addressing` and full lyrics; the current
 * landing schema has no rendered slot for either, so they are not included.
 *
 * ATWA's generated block contains visibly truncated strings for
 * `perspective.description`, `key_lines[0].explanation`,
 * `alternative_readings[0].description`, and `live_tips.listen_for`. The
 * incomplete fragments are not rendered, except for the perspective text before
 * the dangling `Context:` fragment.
 */

import type { SongMeaning } from '@/types/album';

export const songs: SongMeaning[] = [
  {
    n: 1,
    title: 'Prison Song',
    duration: '3:21',
    summary:
      '"Prison Song" es una canción de protesta directa: acusa a la guerra contra las drogas y al encarcelamiento masivo de fabricar una prisión social para todos. Su tensión central está entre el dato frío y el grito comunal, entre la explicación política y la descarga física.',
    perspective:
      'La voz habla desde una mezcla de denuncia callejera y panfleto incendiario. Quiere romper la comodidad del oyente, mostrar que la cárcel, la droga y la guerra forman parte del mismo circuito de poder.',
    voice: 'Acusatoria, urgente, burlona por momentos, con una claridad política poco disimulada.',
    themes: [
      'Encarcelamiento masivo y guerra contra las drogas',
      'Hipocresía estatal y castigo de la pobreza',
      'La prisión como amenaza colectiva',
    ],
    tone: ['furioso', 'satírico'],
    symbols: [
      {
        quote: 'Another prison system',
        meaning:
          'La cárcel es el emblema de un país que responde a la pobreza, la droga y el desorden social con encierro. La canción la expande hasta volverla una condición cotidiana.',
      },
      {
        quote: 'iron fists',
        meaning:
          'Los "iron fists" condensan la reacción autoritaria después de los movimientos por derechos civiles. La represión aparece como respuesta al avance social.',
      },
      {
        quote: 'right here in Hollywood',
        meaning:
          'Hollywood trae la crítica al centro del mito estadounidense. La droga no aparece en un margen invisible, aparece dentro de la fábrica de glamour y consumo.',
      },
      {
        quote: 'now you police the globe',
        meaning:
          'La política global aparece contaminada por el mismo dinero que castiga a usuarios y pequeños vendedores. La canción une prisión local, guerra exterior y control mundial.',
      },
    ],
    key_lines: [
      {
        lyric: "They're trying to build a prison",
        explanation:
          'Este es el grito que la gente espera. La repetición lo vuelve acusación y amenaza compartida: la cárcel ya no queda lejos, en otro barrio o para otra gente, sino que invade la vida civil completa.',
      },
      {
        lyric: 'Minor drug offenders fill your prisons',
        explanation:
          'La línea apunta al corazón político de la canción. El castigo cae sobre delitos menores mientras el sistema se lava las manos con una calma monstruosa.',
      },
      {
        lyric: 'treatment should be increased',
        explanation:
          'Aquí la banda cambia de rabia a propuesta concreta. La canción no se queda en denunciar, también marca una salida: menos encierro, más tratamiento, menos condenas automáticas.',
      },
      {
        lyric: 'secret wars around the world',
        explanation:
          'Esta frase abre el foco. El problema ya no queda limitado a cárceles nacionales: la guerra contra las drogas se conecta con dinero, política exterior y violencia financiada desde arriba.',
      },
    ],
    live: {
      mindset:
        'Rabia colectiva con los ojos abiertos. Se escucha mejor como una protesta que sabe exactamente a quién acusa.',
      listen_for:
        'El choque entre los versos casi recitados, llenos de datos y acusaciones, y el coro de estadio. La canción convierte una crítica muy específica en un canto fácil de gritar.',
      line_to_feel: "They're trying to build a prison",
    },
  },
  {
    n: 3,
    title: 'Deer Dance',
    duration: '2:55',
    summary:
      '"Deer Dance" es una canción de protesta con los dientes apretados: mira una manifestación pacífica y ve cómo el Estado la traduce en amenaza militar. La rabia nace de esa inversión moral, donde los jóvenes piden paz y la autoridad responde empujando a los vulnerables.',
    perspective:
      'La voz mira una protesta reprimida y habla desde el lado de los cuerpos empujados. Quiere que el oyente pierda la comodidad de la distancia. Teme una sociedad donde la autoridad aprende a llamar paz a la intimidación.',
    voice: 'Testigo furioso, coral y sarcástico',
    themes: [
      'brutalidad policial',
      'juventud pacífica frente al poder armado',
      'neutralidad política como complicidad',
    ],
    tone: ['furiosa', 'sarcástica'],
    symbols: [
      {
        quote: 'Beyond the Staples Center',
        meaning:
          'La referencia ubica la canción en Los Ángeles y le da una escala real, casi periodística. Muchos fans la conectan con las protestas alrededor de la Convención Demócrata de 2000, celebrada en el Staples Center.',
      },
      {
        quote: 'rubber bullet kisses',
        meaning:
          'La ternura falsa de la frase vuelve más sucia la violencia. El lenguaje educado cubre golpes, balas de goma y control de multitudes.',
      },
      {
        quote: 'fully automatics',
        meaning:
          'El arma marca el desequilibrio central: niños y jóvenes frente a una fuerza entrenada, blindada y lista para tratar una protesta como guerra.',
      },
      {
        quote: 'plastic existence',
        meaning:
          'Es la crítica a una vida pública esterilizada, de consumo y espectáculo, donde la brutalidad queda tapada por superficies limpias y sonrisas oficiales.',
      },
      {
        quote: 'A deer dance',
        meaning:
          'El ciervo sugiere vulnerabilidad, torpeza bella, algo vivo que no entra en la lógica militar. La paz aquí llega expuesta, casi indefensa.',
      },
    ],
    key_lines: [
      {
        lyric: 'They like to push the weak around',
        explanation:
          'Este es el golpe que el público espera. La frase reduce toda la escena a una verdad fea y cantable: el poder elige blancos fáciles y después lo llama orden.',
      },
      {
        lyric: "We can't afford to be neutral",
        explanation:
          'La línea parte la canción en dos. La neutralidad aparece como una postura cómoda mientras la violencia ya está en marcha y arrastra a todos.',
      },
      {
        lyric: 'A deer dance, invitation to peace',
        explanation:
          'La imagen del título concentra la rareza de System of a Down: una invitación casi inocente a la paz frente a una fuerza que llega vestida para aplastar.',
      },
      {
        lyric: 'The little boy smiled',
        explanation:
          'La sonrisa del niño da miedo por su fragilidad. Mete esperanza infantil en una escena militarizada, y por eso la canción duele más.',
      },
    ],
    live: {
      mindset: 'Rabia colectiva, con el cuerpo en alerta y sin ganas de suavizar nada.',
      listen_for:
        'El salto de los versos tensos al estribillo machacón: ahí la crítica pasa de observación a consigna.',
      line_to_feel: 'They like to push the weak around',
    },
  },
  {
    n: 9,
    title: 'ATWA',
    duration: '2:56',
    summary:
      'ATWA vive en una tensión muy seca: alguien pide reconocimiento mientras confiesa que ya perdió contacto con el mundo. La repetición hace que el dolor parezca mecánico, como si la mente siguiera cantando después de haberse apagado.',
    perspective:
      'La voz canta desde la desconexión. Quiere ser vista, pero ya habla como alguien que se fue por dentro antes de que nadie lo notara.',
    voice: 'Herida, agotada, casi anestesiada.',
    themes: [
      'Desconexión emocional y disociación',
      'Indiferencia ajena ante el sufrimiento',
      'Pérdida progresiva del cuerpo y la voz',
    ],
    tone: ['hipnótico', 'desolado'],
    symbols: [
      {
        quote: 'passing by',
        meaning:
          'El mundo no está presente para la voz, pasa delante como una película ajena. Esa imagen sostiene la sensación de disociación: ver la vida sin poder entrar en ella.',
      },
      {
        quote: "You don't care",
        meaning:
          'El tú concentra la indiferencia. Puede ser una persona concreta, una multitud, una familia, una sociedad, cualquiera que responda al dolor con distancia.',
      },
      {
        quote: "I don't see",
        meaning:
          'La lista de sentidos apagados lleva la canción desde la queja hacia el colapso. Primero hay una herida emocional; después el cuerpo entero empieza a desaparecer.',
      },
      {
        quote: 'Na-na-na-na',
        meaning:
          'El canto sin palabras tiene algo inquietante: parece inocente, pero rodea una letra de vacío y abandono. En directo, esa simpleza vuelve la canción más común, casi ritual.',
      },
    ],
    key_lines: [],
    live: {
      mindset:
        'Duelo colectivo sin solemnidad. La canción pide cantar una ausencia, sentir cómo una frase sencilla puede abrir un hueco enorme.',
      line_to_feel: "I don't feel there anymore",
    },
  },
];
